import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { getLocations } from "../lib/locations";

import Button from "../components/button";
import CollapsedQuestion from "../components/collapsed-question";
import Divider from "../components/divider";
import FeaturedImageSection from "../components/featured-image-section";
import FeaturedLocationsSection from "../components/featured-locations-section";
import Hero from "../components/hero";
import Input from "../components/input";
import Select from "../components/select";
import Stats from "../components/stats";

import { getFAQCategory } from "../lib/faq";
import { getSEOPageProps } from "../lib/seo";

import styles from "./brokers.module.scss";
import SeoMeta from "../components/seo-meta";
import config from "../config";
import TagManager from "react-gtm-module";
import { activeCampaignSubmit } from "../lib/forms";

const fieldMap = {
  membership: "field[89][]",
  location: "field[90][]",
};

export default function BrokersPage({ locations, questions, seo }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "25",
      f: "25",
      s: "0",
      c: "0",
      m: "0",
      act: "sub",
      v: "2",
      or: "564b81e893dd86c98cdcff9715c31fc0",
    },
  });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "brokers",
        ...data,
      },
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };

  const onError = (e) => console.log(e);

  const { errors } = formState;

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  return (
    <>
      <SeoMeta {...seo} />
      <div className="container">
        <Hero
          title="Broker Program"
          description="East Room helps you find great space for your clients in tomorrow’s changing real estate landscape."
        />

        <figure className={styles.FeaturedImage}>
          <Image
            src="/images/brokers.jpg"
            alt="Founding address, 50 Carroll St. (1928)"
            width="1244"
            height="679"
            layout="responsive"
            objectFit="cover"
          />
        </figure>

        <Stats
          stats={[
            {
              number: "68%",
              description:
                "of North American workers prefer a hybrid workplace model.",
              link: "source: Ergonomic Trends “Return to Work Statistics”,2021",
            },
            {
              number: "22%",
              description:
                "of the North American workforce is estimated to be working in flexible office solutions by 2025.",
              link: "source: Global Workplace Analytics “Telework in the 21st Century”, 2021",
            },
            {
              number: "67%",
              description:
                "of CRE decision makers are increasing workspace mobility programs and incorporating flexible space as part of their agile work strategies.",
              link: "source: JLL “Covid and Flex Space Report”, 2020",
            },
          ]}
        />

        <div className={styles.Stats}></div>
        <div className={styles.SectionWrapper}>
          <FeaturedImageSection
            image="/images/private-space.png"
            desktopImage={
              <Image
                src={"/images/private-space-axo.jpg"}
                alt={"Design forward flexible solutions for your clients"}
                width="536"
                height="527"
                layout="responsive"
              />
            }
            mobileImage={
              <Image
                src={"/images/private-space-axo.jpg"}
                alt={"Design forward flexible solutions for your clients"}
                width="335"
                height="329"
                layout="responsive"
              />
            }
            title="Design forward flexible solutions for your clients"
            description="Whether your client is a solo entrepreneur or an established  enterprise, East Room has a variety of design forward flexible solutions to meet their professional needs."
            callToAction={
              <Link href="/solutions" passHref>
                <a>
                  <Button outline rounded small>
                    Explore our solutions
                  </Button>
                </a>
              </Link>
            }
          />
        </div>

        <div className={styles.SectionWrapper}>
          <FeaturedImageSection
            reverse
            desktopImage={
              <Image
                src={"/images/brokers-features.svg"}
                alt={"Design forward flexible solutions for your clients"}
                width="551"
                height="414"
                layout="responsive"
              />
            }
            mobileImage={
              <Image
                src={"/images/brokers-features-mobile.svg"}
                alt={"Design forward flexible solutions for your clients"}
                width="335"
                height="396"
                layout="responsive"
              />
            }
            title="Save money and simplify operations"
            mobileDescriptionFirst
            description="One predictable monthly fee can help mitigate financial risks of hidden costs while reducing CapEx in ways traditional leases can't."
          />
        </div>
      </div>

      <FeaturedLocationsSection locations={locations} />

      <div className="container">
        <div className={styles.BrokerFormWrapper}>
          <div className={styles.BrokerFormContent}>
            <h2 className="font-bold">Contact our sales team</h2>

            <p>
              Submit the form and one of our sales representatives will get in
              touch about available inventory and leveraging East Room’s network
              and platform.
            </p>
          </div>

          {submitted && (
            <p className={styles.ConfirmationMessage}>
              Thank you, we will be in touch shortly!
            </p>
          )}

          {!submitted && (
            <form
              className={styles.BrokerForm}
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="flex flex-row justify-between items-center gap-2">
                <Input
                  type="text"
                  placeholder="First name *"
                  {...register("firstname", {
                    required: "First name is required",
                  })}
                  errorText={errors.firstname && errors.firstname.message}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Last name *"
                  {...register("lastname", {
                    required: "Last name is required",
                  })}
                  errorText={errors.lastname && errors.lastname.message}
                  inline
                />
              </div>
              <div>
                <Input
                  type="text"
                  placeholder="Email address *"
                  {...register("email", { required: "Email is required" })}
                  errorText={errors.email && errors.email.message}
                />
              </div>
              <div>
                <Input
                  type="text"
                  placeholder="Company name *"
                  {...register("phone", {
                    required: "Company name is required",
                  })}
                  errorText={errors.phone && errors.phone.message}
                />
              </div>
              <div>
                <Select
                  type="text"
                  placeholder=""
                  {...register(fieldMap.location, {
                    required: "Location is required",
                  })}
                  errorText={getError(fieldMap.location)}
                >
                  <option value="" disabled selected>
                    Locations interested in *
                  </option>
                  {locations.map((location) => (
                    <option
                      value={`${location.address1}, ${location.city} ${location.province}`}
                      key={location.slug}
                    >
                      {location.address1}, {location.city} {location.province}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  type="text"
                  {...register(fieldMap.membership, {
                    required: "Membership is required",
                  })}
                  errorText={getError(fieldMap.membership)}
                >
                  <option value="" disabled selected>
                    Memberships interested in *
                  </option>

                  <option value="Mailbox">Mailbox</option>
                  <option value="Social Membership">Social Membership</option>
                  <option value="Club Membership">Club Membership</option>
                  <option value="Dedicated Desk">Dedicated Desk</option>
                  <option value="2 Person Office">2 Person Office</option>
                  <option value="Standard Office">Standard Office</option>
                  <option value="Office Suite">Office Suite</option>
                  <option value="Premium Office">Premium Office</option>
                </Select>
              </div>

              {error && <p>{error}</p>}

              {/* <div className="text-center md:text-left mt-4"> */}
              <div className={styles.BrokerFormButtonWrapper}>
                <Button rounded outline small type="submit">
                  Get in Touch
                </Button>
              </div>
            </form>
          )}
        </div>

        <Divider />

        <section className={styles.BrokerQuestionsSection}>
          <div className={styles.BrokerQuestionHeader}>
            <h3>Have a question?</h3>
          </div>
          <div className={styles.BrokerQuestionContent}>
            {questions.map((question) => (
              <CollapsedQuestion
                key={question.title}
                question={question.title}
                answer={question.description}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export async function getStaticProps() {
  try {
    const [locations, questions, seo] = await Promise.all([
      getLocations(),
      getFAQCategory("brokers"),
      getSEOPageProps({ resolvedUrl: "/brokers" }),
    ]);

    return {
      props: {
        locations,
        questions,
        seo,
      },
      revalidate: config.ISR_TIMING,
    };
  } catch (e) {
    console.log(e.message);
  }
}
